'use client';

import React, { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { getCountryName, getCurrencyForCountry, useCountryDetection } from '@/utils/countryDetection';

/** The countries the /pricing picker offers (the one picker that calls setCountryCode), in its order. */
export const COUNTRY_CODES = [
  'US', 'CA', 'MX', 'BR',
  'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'CH', 'SE', 'NO', 'DK', 'PL', 'IE',
  'AU', 'NZ', 'JP', 'SG', 'IN',
  'AE', 'SA', 'KW', 'QA', 'BH', 'OM',
  'ZA', 'IL',
] as const;

/**
 * Native country/currency picker (header utility strip and phone menu). Shows a short "US · USD" face; the
 * transparent native <select> laid over it carries the label and the full names, and moves every price on the
 * page through countryStore.setCountryCode. Server render and first client render show US (the store's server
 * snapshot), so hydration matches. `compact` = the 34px ink strip; otherwise a 44px tap target on paper.
 */
export default function CountrySelect({ compact = false }: { compact?: boolean }) {
  const id = useId();
  const { country, setCountryCode } = useCountryDetection();
  const code = country?.code && (COUNTRY_CODES as readonly string[]).includes(country.code) ? country.code : 'US';
  const box = compact
    ? 'opacity-90 hover:opacity-100 focus-within:outline-paper'
    : 'min-h-[44px] focus-within:outline-marigold-deep';

  return (
    <span className={`relative inline-flex items-center gap-1.5 rounded focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 ${box}`}>
      <label htmlFor={id} className="sr-only">Country / currency</label>
      <span aria-hidden="true">{code} · {getCurrencyForCountry(code)}</span>
      <ChevronDown aria-hidden="true" className="h-3 w-3" />
      <select
        id={id}
        value={code}
        onChange={(e) => setCountryCode(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c} value={c}>{getCountryName(c)} ({getCurrencyForCountry(c)})</option>
        ))}
      </select>
    </span>
  );
}
